import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const FIELD_TYPES = ["text", "date", "number", "boolean", "dropdown", "language"];

const formatFieldTypeLabel = (type) => {
  const value = String(type || "").trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
};

const shouldAutoUseLanguageType = (placeholder, label) => {
  const combined = `${placeholder || ""} ${label || ""}`.toLowerCase();
  return combined.includes("language");
};

const smallGreyTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(128, 128, 128, 0.45)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(128, 128, 128, 0.65)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(128, 128, 128, 0.9)",
    },
  },
};

const ServiceDocumentNewEdit = () => {
  usePageTitle("Service Document");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const [activeDocumentId] = useState(overlayData?.documentId || null);
  const documentId = activeDocumentId;
  const isEditMode = !!documentId;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(overlayData?.document?.Name || "");
  const [description, setDescription] = useState(overlayData?.document?.Description || "");
  const [isActive, setIsActive] = useState(
    overlayData?.document?.IsActive === undefined ? true : !!overlayData?.document?.IsActive,
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [fields, setFields] = useState([]);
  const [previewFields, setPreviewFields] = useState([]);
  const [detectingFields, setDetectingFields] = useState(false);

  const isUploadFirstState = !isEditMode && !selectedFile;

  const closeOverlay = () => setOverlayName("");

  useEffect(() => {
    if (!isEditMode) return;

    let mounted = true;
    const loadDocument = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/serviceDocuments/${documentId}`);
        if (!mounted) return;

        const document = response.data || {};
        setName(document.Name || "");
        setDescription(document.Description || "");
        setIsActive(document.IsActive === undefined ? true : !!document.IsActive);
        setFields(Array.isArray(document.Fields) ? document.Fields : []);
      } catch (error) {
        showSnackbar("Failed to load service document", theme, "error");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDocument();
    return () => {
      mounted = false;
    };
  }, [documentId, isEditMode, showSnackbar, theme]);

  const refreshFields = async () => {
    if (!documentId) return;
    const response = await api.get(`/serviceDocuments/${documentId}`);
    const document = response.data || {};
    setFields(Array.isArray(document.Fields) ? document.Fields : []);
  };

  const detectFieldsFromFile = async (file) => {
    if (!file) {
      setPreviewFields([]);
      return;
    }

    try {
      setDetectingFields(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/serviceDocuments/detect-placeholders", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const detected = Array.isArray(response?.data?.fields) ? response.data.fields : [];
      setPreviewFields(detected);
    } catch (error) {
      setPreviewFields([]);
      showSnackbar(error?.response?.data?.error || "Failed to detect placeholders", theme, "error");
    } finally {
      setDetectingFields(false);
    }
  };

  const updatePreviewFieldLocal = (placeholder, patch) => {
    setPreviewFields((previous) =>
      previous.map((field) => {
        if (field.OriginalPlaceholder !== placeholder) {
          return field;
        }

        const nextField = { ...field, ...patch };
        if (
          Object.prototype.hasOwnProperty.call(patch, "UserFriendlyLabel") &&
          shouldAutoUseLanguageType(nextField.OriginalPlaceholder, nextField.UserFriendlyLabel)
        ) {
          nextField.FieldType = "language";
        }

        return nextField;
      }),
    );
  };

  const handleSaveDocument = async () => {
    console.log("[ServiceDocuments][Client] Save clicked", {
      isEditMode,
      hasSelectedFile: !!selectedFile,
      selectedFileName: selectedFile?.name || null,
      name,
      descriptionLength: String(description || "").length,
    });

    if (!name.trim()) {
      showSnackbar("Name is required", theme, "error");
      return;
    }

    if (!isEditMode && !selectedFile) {
      showSnackbar("DOCX file is required", theme, "error");
      return;
    }

    try {
      setSaving(true);

      if (!isEditMode) {
        const formData = new FormData();
        formData.append("Name", name.trim());
        formData.append("Description", description.trim());
        formData.append("file", selectedFile);
        formData.append(
          "fieldConfigs",
          JSON.stringify(
            previewFields.map((field) => ({
              OriginalPlaceholder: field.OriginalPlaceholder,
              UserFriendlyLabel: (field.UserFriendlyLabel || "").trim() || field.OriginalPlaceholder,
              FieldType: field.FieldType,
              IsRequired: !!field.IsRequired,
              DefaultValue: field.DefaultValue ?? null,
            })),
          ),
        );

        showSnackbar("Uploading document...", theme, "info");

        const createResponse = await api.post("/serviceDocuments/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (createResponse?.data?.template?.id && typeof overlayData?.onSaved === "function") {
          overlayData.onSaved();
        }

        showSnackbar("Service document created", theme, "success");
        closeOverlay();
      } else {
        await api.put(`/serviceDocuments/${documentId}`, {
          Name: name.trim(),
          Description: description.trim(),
          IsActive: isActive,
        });

        showSnackbar("Service document updated", theme, "success");

        if (typeof overlayData?.onSaved === "function") {
          overlayData.onSaved();
        }

        closeOverlay();
      }
    } catch (error) {
      console.error("[ServiceDocuments][Client] Save failed", {
        message: error?.message,
        responseData: error?.response?.data,
        status: error?.response?.status,
      });
      showSnackbar(error?.response?.data?.error || "Failed to save service document", theme, "error");
    } finally {
      setSaving(false);
    }
  };

  const updateFieldLocal = (fieldId, patch) => {
    setFields((previous) =>
      previous.map((field) => {
        if (field.id !== fieldId) {
          return field;
        }

        const nextField = { ...field, ...patch };
        if (
          Object.prototype.hasOwnProperty.call(patch, "UserFriendlyLabel") &&
          shouldAutoUseLanguageType(nextField.OriginalPlaceholder, nextField.UserFriendlyLabel)
        ) {
          nextField.FieldType = "language";
        }

        return nextField;
      }),
    );
  };

  const handleSaveField = async (field) => {
    try {
      await api.put(`/serviceDocuments/${documentId}/fields/${field.id}`, {
        UserFriendlyLabel: field.UserFriendlyLabel,
        FieldType: field.FieldType,
        IsRequired: !!field.IsRequired,
      });
      showSnackbar(`Updated ${field.UserFriendlyLabel}`, theme, "success");
      await refreshFields();
    } catch (error) {
      showSnackbar(error?.response?.data?.error || "Failed to update field", theme, "error");
    }
  };

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "92%", md: "85%", lg: "75%" },
          height: { xs: "95%", sm: "86%" },
          maxWidth: "1300px",
          maxHeight: "95%",
          display: "flex",
          borderRadius: "1.5rem",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: "0.75rem 2rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton color="primary" size="large" onClick={closeOverlay}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {isEditMode ? "Edit Service Document" : "New Service Document"}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={handleSaveDocument}
            disabled={saving || loading}
            sx={{ borderRadius: "10px" }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            width: "100%",
            overflowY: "auto",
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
          }}
        >
          {loading ? (
            <Typography>Loading...</Typography>
          ) : isUploadFirstState ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                px: 2,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                size="large"
                sx={{
                  borderRadius: "10px",
                  minWidth: 220,
                  minHeight: 48,
                }}
              >
                Upload DOCX
                <input
                  type="file"
                  accept=".docx"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setSelectedFile(file);
                    detectFieldsFromFile(file);
                    if (file && !name.trim()) {
                      const baseName = String(file.name || "").replace(/\.docx$/i, "").trim();
                      setName(baseName || "");
                    }
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary" align="center">
                Select a DOCX template first. Name and Description will appear next.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {!isEditMode && (
                <Alert severity="info">
                  Upload a DOCX file with placeholders in this format: {'{ClientName}'}.
                </Alert>
              )}

              <Grid container spacing={2}>
                {!isEditMode && (
                  <Grid size={{ xs: 12 }}>
                    <Button variant="outlined" component="label" sx={{ borderRadius: "10px" }}>
                      {selectedFile ? selectedFile.name : "Upload DOCX"}
                      <input
                        type="file"
                        accept=".docx"
                        hidden
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setSelectedFile(file);
                          detectFieldsFromFile(file);
                          if (file && !name.trim()) {
                            const baseName = String(file.name || "").replace(/\.docx$/i, "").trim();
                            setName(baseName || "");
                          }
                        }}
                      />
                    </Button>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                    sx={smallGreyTextFieldSx}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    sx={smallGreyTextFieldSx}
                  />
                </Grid>
                {isEditMode && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
                      <Typography>{isActive ? "Active" : "Archived"}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {isEditMode && (
                <>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
                    Fields
                  </Typography>
                  {fields.length === 0 ? (
                    <Typography color="text.secondary">No placeholders detected.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {fields.map((field) => (
                        <Box
                          key={field.id}
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Placeholder
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {`{${field.OriginalPlaceholder}}`}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Label"
                                value={field.UserFriendlyLabel || ""}
                                onChange={(event) =>
                                  updateFieldLocal(field.id, { UserFriendlyLabel: event.target.value })
                                }
                                sx={smallGreyTextFieldSx}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                label="Type"
                                value={field.FieldType || "text"}
                                onChange={(event) => updateFieldLocal(field.id, { FieldType: event.target.value })}
                                sx={smallGreyTextFieldSx}
                              >
                                {FIELD_TYPES.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {formatFieldTypeLabel(type)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                <Switch
                                  checked={!!field.IsRequired}
                                  onChange={(event) =>
                                    updateFieldLocal(field.id, { IsRequired: event.target.checked })
                                  }
                                />
                                <Typography variant="caption">Required</Typography>
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }}>
                              <Button size="small" onClick={() => handleSaveField(field)}>
                                Save
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}

              {!isEditMode && selectedFile && (
                <>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                    <Typography variant="h4" fontWeight={700}>
                      Fields
                    </Typography>
                  </Box>
                  {detectingFields ? (
                    <Typography color="text.secondary">Detecting placeholders...</Typography>
                  ) : previewFields.length === 0 ? (
                    <Typography color="text.secondary">No placeholders detected in selected DOCX.</Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {previewFields.map((field, idx) => (
                        <Box
                          key={`${field.OriginalPlaceholder}-${idx}`}
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: theme.palette.background.default,
                          }}
                        >
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Placeholder
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {`{${field.OriginalPlaceholder}}`}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Label"
                                value={field.UserFriendlyLabel || ""}
                                onChange={(event) =>
                                  updatePreviewFieldLocal(field.OriginalPlaceholder, {
                                    UserFriendlyLabel: event.target.value,
                                  })
                                }
                                sx={smallGreyTextFieldSx}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <TextField
                                fullWidth
                                select
                                size="small"
                                label="Type"
                                value={field.FieldType || "text"}
                                onChange={(event) =>
                                  updatePreviewFieldLocal(field.OriginalPlaceholder, {
                                    FieldType: event.target.value,
                                  })
                                }
                                sx={smallGreyTextFieldSx}
                              >
                                {FIELD_TYPES.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {formatFieldTypeLabel(type)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 6, md: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                                <Switch
                                  checked={!!field.IsRequired}
                                  onChange={(event) =>
                                    updatePreviewFieldLocal(field.OriginalPlaceholder, {
                                      IsRequired: event.target.checked,
                                    })
                                  }
                                />
                                <Typography variant="caption">Required</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceDocumentNewEdit;
